import { useState, useEffect } from 'react';
import { usePWA, useDeviceType } from '@/hooks/usePWA';
import { Button } from '@/components/ui/Button';

interface InstallPromptProps {
  variant?: 'banner' | 'modal' | 'inline';
}

export function InstallPrompt({ variant = 'banner' }: InstallPromptProps) {
  const { isInstallable, installApp, isInstalled } = usePWA();
  const deviceType = useDeviceType();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  // Vérifier si déjà rejeté dans cette session
  useEffect(() => {
    const dismissed = sessionStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      setIsDismissed(true);
    }
  }, []);

  const handleInstall = async () => {
    setIsInstalling(true);
    const success = await installApp();
    setIsInstalling(false);

    if (!success) {
      // L'utilisateur a refusé, ne pas redemander dans cette session
      sessionStorage.setItem('pwa-install-dismissed', 'true');
      setIsDismissed(true);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem('pwa-install-dismissed', 'true');
    setIsDismissed(true);
  };

  // Ne pas afficher si non installable, déjà installé ou rejeté
  if (!isInstallable || isInstalled || isDismissed) {
    return null;
  }

  if (variant === 'banner') {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-orange-500 to-red-500 text-white p-4 shadow-lg z-50 safe-area-inset-bottom">
        <div className="max-w-lg mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-full p-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-sm">Installer l'application</p>
              <p className="text-xs text-white/80">
                {deviceType === 'mobile' ? 'Accès rapide depuis votre écran d\'accueil' : 'Accès hors-ligne et notifications'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="text-white hover:bg-white/20"
            >
              Plus tard
            </Button>
            <Button
              size="sm"
              onClick={handleInstall}
              disabled={isInstalling}
              className="bg-white text-orange-600 hover:bg-white/90"
            >
              {isInstalling ? 'Installation...' : 'Installer'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'modal') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden shadow-xl">
          {/* Header avec illustration */}
          <div className="bg-gradient-to-br from-orange-400 to-red-500 p-6 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl mx-auto mb-4 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold">Installer l'application</h2>
          </div>

          {/* Contenu */}
          <div className="p-6">
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-3 text-gray-700">
                <span className="text-green-500">✓</span>
                <span>Accès rapide depuis l'écran d'accueil</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <span className="text-green-500">✓</span>
                <span>Fonctionne hors connexion</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <span className="text-green-500">✓</span>
                <span>Notifications pour les alertes de stock</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <span className="text-green-500">✓</span>
                <span>Expérience plein écran</span>
              </li>
            </ul>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleDismiss}
                className="flex-1"
              >
                Plus tard
              </Button>
              <Button
                onClick={handleInstall}
                disabled={isInstalling}
                className="flex-1"
              >
                {isInstalling ? 'Installation...' : 'Installer'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Variant inline
  return (
    <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-4">
      <div className="flex items-center gap-4">
        <div className="bg-orange-100 rounded-full p-3">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900">Installer l'application</h3>
          <p className="text-sm text-gray-600">Accédez rapidement à votre inventaire, même hors ligne</p>
        </div>
        <Button onClick={handleInstall} disabled={isInstalling}>
          {isInstalling ? '...' : 'Installer'}
        </Button>
      </div>
    </div>
  );
}
